import { parseProfile } from "@/features/auth/api/profile-parsers"
import type { Profile } from "@/features/auth/api/types"
import { apiClient } from "@/react-query/api-client"
import type { ApiEnvelope, PagedData } from "@/react-query/api-types"

export type GetUsersSortDirection = "asc" | "desc"

export type GetUsersResponse = {
  content: Profile[]
  totalElements: number
  page: number
  size: number
  totalPages: number
  hasMore: boolean
}

export type AccountsListParams = {
  query?: string
  tagIds?: number[]
  sortBy?: string
  sortDirection?: GetUsersSortDirection
  size?: number
  /** Internal accounts only — comma-separated account types. */
  accountTypes?: string
  /** Streamer accounts only. */
  managerId?: string
}

/** Shared page mapper for admin account list endpoints. */
export async function fetchAccountsPage(
  route: string,
  params: AccountsListParams,
  page: number
): Promise<GetUsersResponse> {
  const response = await apiClient<ApiEnvelope<PagedData<Profile>>>(route, {
    params: {
      ...(params.accountTypes && { accountTypes: params.accountTypes }),
      ...(params.query?.trim() && { query: params.query.trim() }),
      ...(params.tagIds?.length && { tagIds: params.tagIds.join(",") }),
      ...(params.managerId && {
        managerId: params.managerId,
      }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortDirection && {
        sortDirection: params.sortDirection,
      }),
      page,
      size: params.size ?? 20,
    },
  })
  const { content, totalElements } = response.data
  const size = response.data.size ?? params.size ?? 20
  const resolvedPage = response.data.page ?? page

  return {
    content: content.map(parseProfile),
    totalElements,
    page: resolvedPage,
    size,
    totalPages:
      response.data.totalPages ??
      (size > 0 ? Math.ceil(totalElements / size) : 0),
    hasMore: (resolvedPage + 1) * size < totalElements,
  }
}
